import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'

import { selectHeardProductSubscription } from '@/reducers/subscription.slice'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  CancellationStepIdentifier,
  updateAutomaticCancellationAnalyticsRecord,
} from './lib/userCancellation.slice'

import { ReactivatePlanModal } from '@/v2-features/user/settings/sections/self-serve-cancellation/ReactivatePlanModal'
import { TypographyStyles } from '@/styles'
import { Button, Card, CardColors, Image, Link, Text } from '@/ui'

export const CancellationComplete = ({ recordId }: { recordId?: number }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [reactivateOpen, setReactivateOpen] = useState(false)

  const sub = useReselector(selectHeardProductSubscription)

  useEffect(() => {
    const updateRecord = async (recordId?: number) => {
      if (recordId) {
        await updateAutomaticCancellationAnalyticsRecord(recordId, {
          finalStepReached: CancellationStepIdentifier.CANCELLATION_COMPLETE,
        })(dispatch)
      }
    }
    updateRecord(recordId)
  }, [dispatch, recordId])

  //nextBillingDate is set to the current period end in the slice
  const formattedBillingDate =
    sub?.nextBillingDate &&
    DateTime.fromSeconds(sub?.nextBillingDate).toFormat(
      DATE_FORMATS_LUXON.DISPLAY_SIMPLE
    )

  return (
    <div className="flex flex-col items-center space-y-8">
      <div className="flex justify-center mt-8">
        <Image
          alt="Checkmark"
          src="https://heard-images.s3.amazonaws.com/assets/check.svg"
          width={180}
          height={180}
        />
      </div>

      <Text variant={TypographyStyles.H1}>Goodbye for now!</Text>

      <Text
        variant={TypographyStyles.BODY_LG}
        className="w-full md:w-1/2 text-center mx-auto"
      >
        The last day of your subscription will be {formattedBillingDate}. We
        hope you&apos;ll join us again when the timing is right!
      </Text>

      <Button onClick={() => setReactivateOpen(true)}>
        Reactivate Subscription
      </Button>

      <Button variant="secondary" onClick={() => navigate('/accounts')}>
        Back to Settings
      </Button>

      <Card color={CardColors.EMPHASIS} className="w-full md:w-1/2 mx-auto">
        <Text variant={TypographyStyles.H2}>Need to download your data?</Text>

        <Text variant={TypographyStyles.BODY_MD} className="mt-4">
          Visit your{' '}
          <Link variant="secondary" href="/accounts">
            account settings
          </Link>{' '}
          page, scroll down to the &quot;Plan & Billing&quot; section, and click
          &quot;Download Account Information.&quot; You&apos;ll then receive an
          email when your information is ready to download with a link to do so
        </Text>
      </Card>

      {reactivateOpen && (
        <ReactivatePlanModal
          open={reactivateOpen}
          close={() => setReactivateOpen(false)}
          redirectUrl="/accounts"
        />
      )}
    </div>
  )
}
